import { ClipboardCheckIcon, HandshakeIcon, ShoppingCartIcon, TruckIcon } from 'lucide-react'
import type { ProductPageContent } from '../../types/product'
import { industryCatalog, productCatalog } from './shared'

export const purchases: ProductPageContent = {
  slug: 'purchases',
  name: 'Purchases',
  icon: ShoppingCartIcon,
  hero: {
    headline: 'Order from suppliers on time, at the right price',
    subheadline:
      'Create purchase orders in seconds, track deliveries, and pay suppliers on schedule — with stock updated the moment goods arrive.',
    trustLine: 'Connected to your stock and payments · Used by 4,200+ businesses',
    visual: {
      kind: 'devices',
      tablet: {
        module: 'Purchases',
        greeting: 'Purchases · September',
        kpis: [
          { label: 'Open orders', value: '9', delta: { label: '3 arriving today', tone: 'brand' } },
          { label: 'Spent this month', value: 'RWF 7.2M' },
          { label: 'Due to suppliers', value: 'RWF 2.1M', delta: { label: '2 due this week', tone: 'warning' } },
        ],
        main: {
          type: 'list',
          title: 'Purchase orders',
          rows: [
            { label: 'PO-0418 · Kivu Distributors', sublabel: 'Arriving today', value: 'RWF 1,240,000' },
            { label: 'PO-0417 · Nyabugogo Wholesale', sublabel: 'Received', value: 'RWF 684,500', tone: 'positive' },
            { label: 'PO-0416 · Huye Dairy Co-op', sublabel: 'Partly received', value: 'RWF 312,000', tone: 'warning' },
          ],
        },
        side: {
          type: 'progress',
          title: 'Top suppliers',
          rows: [
            { label: 'Kivu Distributors', value: '42%', pct: 42 },
            { label: 'Nyabugogo Wholesale', value: '28%', pct: 28 },
            { label: 'Huye Dairy Co-op', value: '14%', pct: 14 },
          ],
        },
      },
      phone: {
        type: 'list',
        title: 'Delivery arrived',
        meta: 'PO-0418 · Kivu Distributors',
        rows: [
          { label: 'Rice 5kg', value: '40 / 40', tone: 'positive' },
          { label: 'Cooking oil 1L', value: '48 / 48', tone: 'positive' },
          { label: 'Sugar 1kg', value: '55 / 60', tone: 'warning' },
        ],
        action: 'Receive into stock',
      },
    },
  },
  stats: [
    { value: '4,200+', label: 'businesses ordering from suppliers with Kayko' },
    { value: 'RWF 3B+', label: 'in purchase orders raised every month' },
    { value: '5 hrs', label: 'saved each week on ordering and follow-up' },
  ],
  benefits: {
    heading: 'Buying made simple.',
    items: [
      { icon: ClipboardCheckIcon, title: 'Orders in seconds', copy: 'Turn low-stock alerts into purchase orders and send them to suppliers straight away.' },
      { icon: TruckIcon, title: 'Deliveries you can check', copy: 'Receive goods against the order and see exactly what arrived, what’s missing, and what’s late.' },
      { icon: HandshakeIcon, title: 'Suppliers paid on time', copy: 'Track what you owe each supplier and pay by mobile money or bank transfer.' },
    ],
  },
  features: {
    heading: 'From order to shelf, without the paperwork.',
    subheading: 'Built for shops, restaurants, and workshops that buy from several suppliers.',
    items: [
      {
        title: 'Purchase orders in a few taps',
        copy: 'Build an order from suggested quantities or pick products yourself. Send it to your supplier by SMS, email, or as a PDF.',
        bullets: ['Suggested quantities based on recent sales', 'Supplier price lists saved per product', 'Approval step for large orders'],
        visual: {
          kind: 'card',
          card: {
            type: 'list',
            title: 'New order · Kivu Distributors',
            meta: 'Delivery by 22 Sep',
            rows: [
              { label: 'Rice 5kg', sublabel: '40 × RWF 6,800', value: 'RWF 272,000' },
              { label: 'Cooking oil 1L', sublabel: '48 × RWF 2,500', value: 'RWF 120,000' },
              { label: 'Sugar 1kg', sublabel: '60 × RWF 1,550', value: 'RWF 93,000' },
            ],
            footer: { label: 'Order total', value: 'RWF 485,000' },
            action: 'Send to supplier',
          },
        },
      },
      {
        title: 'Receive deliveries with confidence',
        copy: 'Check deliveries against the order line by line. Stock updates automatically, and shortages are recorded for follow-up.',
        bullets: ['Partial deliveries and back-orders', 'Record damaged or rejected items', 'Stock updated the moment goods arrive'],
        visual: {
          kind: 'card',
          card: {
            type: 'progress',
            title: 'Receiving PO-0416',
            meta: 'Huye Dairy Co-op',
            badge: { label: 'Partly received', tone: 'warning' },
            rows: [
              { label: 'Milk 500ml', value: '120 / 120', pct: 100, tone: 'positive' },
              { label: 'Yoghurt 500ml', value: '36 / 48', pct: 75, tone: 'warning' },
              { label: 'Butter 250g', value: '0 / 24', pct: 0, tone: 'negative' },
            ],
            action: 'Save and request remaining',
          },
        },
      },
      {
        title: 'Supplier balances and payments',
        copy: 'See what you owe each supplier and when it’s due. Record payments by mobile money, bank transfer, or cash, and keep every invoice attached.',
        bullets: ['Due dates and payment reminders', 'Invoice photos stored with each order', 'Supplier statements in one click'],
        visual: {
          kind: 'card',
          card: {
            type: 'list',
            title: 'Supplier balances',
            rows: [
              { label: 'Kivu Distributors', sublabel: 'Due 20 Sep', value: 'RWF 1,240,000', tone: 'warning' },
              { label: 'Nyabugogo Wholesale', sublabel: 'Due 28 Sep', value: 'RWF 684,500' },
              { label: 'Huye Dairy Co-op', sublabel: 'Paid 15 Sep', value: 'Settled', tone: 'positive' },
            ],
            footer: { label: 'Total owed', value: 'RWF 1,924,500' },
          },
        },
      },
      {
        title: 'Compare suppliers and prices',
        copy: 'See how much you buy from each supplier and how their prices change over time. Negotiate with real numbers instead of guesses.',
        bullets: ['Price history for every product', 'Spend by supplier and category', 'Delivery reliability for each supplier'],
        visual: {
          kind: 'card',
          card: {
            type: 'bars',
            title: 'Cooking oil 1L · cost price',
            value: 'RWF 2,500',
            delta: { label: '−4% vs last quarter', tone: 'positive' },
            bars: [
              { label: 'Apr', value: 2650 },
              { label: 'May', value: 2700 },
              { label: 'Jun', value: 2600 },
              { label: 'Jul', value: 2550 },
              { label: 'Aug', value: 2520 },
              { label: 'Sep', value: 2500, highlight: true },
            ],
            legend: 'Lowest price: Kivu Distributors',
          },
        },
      },
    ],
  },
  steps: {
    heading: 'Up and running in an afternoon.',
    items: [
      { title: 'Add your suppliers', copy: 'Save supplier contacts, payment terms, and the products you buy from each.' },
      { title: 'Send your first order', copy: 'Create an order from low-stock suggestions and send it by SMS or email.' },
      { title: 'Receive and pay', copy: 'Check the delivery, update stock automatically, and record the payment.' },
    ],
  },
  related: {
    heading: 'Works with the rest of Kayko.',
    subheading: 'Every order connects to your stock, costs, and cash flow.',
    items: [productCatalog.inventory, productCatalog.expenses, productCatalog.reports, productCatalog['business-finance']],
  },
  industries: {
    heading: 'Built for your industry.',
    subheading: 'Purchases fits the way you buy stock, ingredients, and materials.',
    items: [industryCatalog.retail, industryCatalog.food, industryCatalog.manufacturing, industryCatalog.healthcare],
  },
  testimonial: {
    quote:
      'We buy timber, glue, and fittings from eight suppliers. With Kayko every order, delivery, and payment is in one place, and we no longer pay twice for the same invoice.',
    name: 'Emmanuel Twagirayezu',
    role: 'Production Manager',
    business: 'Ubumwe Furniture Works',
    location: 'Masoro, Kigali',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/a4f4c3d3-2a3c-4598-9448-00bb4f94a87c.jpg',
    metric: { value: 'RWF 2.6M', label: 'saved on materials in the first year' },
  },
  pricingTeaser: { text: 'Purchases is included in every Kayko plan.', linkLabel: 'See pricing', href: '#' },
  faq: {
    heading: 'Frequently asked questions',
    items: [
      { question: 'How do suppliers receive my orders?', answer: 'Send orders by SMS, email, or as a PDF you can share on any messaging app. Suppliers don’t need a Kayko account.' },
      { question: 'Can I handle partial deliveries?', answer: 'Yes. Receive what arrived and Kayko keeps the rest open as a back-order until it’s delivered or cancelled.' },
      { question: 'Can I pay suppliers from Kayko?', answer: 'You can record payments made by mobile money, bank transfer, or cash, and keep the balance with each supplier up to date.' },
      { question: 'Can I require approval for large orders?', answer: 'Yes. Set an amount above which a manager or owner must approve before the order is sent.' },
      { question: 'Does it work with imported goods?', answer: 'Yes. Record orders in other currencies and add transport and clearing costs so your product costs stay accurate.' },
      { question: 'Can I import my supplier list?', answer: 'Yes. Upload suppliers and their price lists from a spreadsheet, and our team can help you set up.' },
    ],
  },
  cta: {
    headline: 'Order smarter from today.',
    subheadline: 'Add your suppliers in minutes, with support from our team in Kigali.',
  },
}
